import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';
import { fetchGHGInventoryData } from '../components/charts/GHGInventorySupabase';
import { fetchCaliforniaConsumptionBasedSummedFromCounties } from './consumptionBasedGHGService';
import { CALIFORNIA_STATE_GEOFIPS } from './californiaGhgInventoryVmtService';

export interface InventoryComparisonResult {
  chartData: {
    labels: number[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor: string | string[];
      borderColor: string | string[];
      fill?: boolean;
      tension?: number;
      borderWidth?: number;
      pointRadius?: number;
      pointHoverRadius?: number;
    }>;
  };
  chartOptions: any;
  sectorBasedTotal: number;
  /** Modeled community total from CBEI (not comparable to sector total). */
  consumptionBasedTotal: number;
  /** Per modeled household MT CO₂e for latest aligned year, when CBEI rows provide tract-level per-HH emissions. */
  consumptionPerHouseholdMtCo2e: number | null;
  comparisonCompatibility: 'different_accounting_boundaries';
  canDifference: false;
  latestYear: number;
}

export async function fetchInventoryComparisonData(location: string): Promise<InventoryComparisonResult> {
  try {
    // Get geofips for the location
    const geofips = await supabaseLocationService.getGeofips(location);
    if (!geofips) {
      throw new Error(`Could not find geofips for location: ${location}`);
    }
    
    // Get location info to determine if it's a city or county
    const { data: locationData, error: locationError } = await supabaseClient
      .from('population')
      .select('location_name, geography_type, county')
      .eq('geofips', geofips)
      .limit(1);
    
    if (locationError) {
      console.error('Error fetching location data:', locationError);
      throw locationError;
    }
    
    if (!locationData || locationData.length === 0) {
      throw new Error(`No location data found for geofips: ${geofips}`);
    }
    
    const locationInfo = locationData[0];

    // Fetch sector-based GHG inventory data
    const sectorBasedResult = await fetchGHGInventoryData(location);

    const consumptionByYear: { [year: number]: number } = {};
    const consumptionPerHouseholdByYear: { [year: number]: number } = {};

    const isCaliforniaState =
      locationInfo.geography_type === 'State' && Number(geofips) === CALIFORNIA_STATE_GEOFIPS;

    if (isCaliforniaState) {
      const caSum = await fetchCaliforniaConsumptionBasedSummedFromCounties();
      if (!caSum.length) {
        throw new Error(
          'No consumption-based GHG data for California (no cbei_counties rows in the CA county band).'
        );
      }
      for (const d of caSum) {
        consumptionByYear[d.year] =
          d.transportation + d.housing + d.food + d.goods + d.services;
      }
    } else {
      let tableName: string;
      const queryGeofips = geofips;

      if (locationInfo.geography_type === 'City') {
        tableName = 'cbei_cities';
      } else if (locationInfo.geography_type === 'County') {
        tableName = 'cbei_counties';
      } else {
        throw new Error(`Unsupported geography type: ${locationInfo.geography_type}`);
      }

      const { data: consumptionData, error: consumptionError } = await supabaseClient
        .from(tableName)
        .select(
          'year, transportation_total_emissions, housing_total_emissions, food_total_emissions, goods_total_emissions, services_total_emissions, hhcount'
        )
        .eq('geofips', queryGeofips)
        .order('year', { ascending: true });

      if (consumptionError) {
        console.error(`Error fetching consumption-based GHG data from ${tableName}:`, consumptionError);
        throw consumptionError;
      }

      if (!consumptionData || consumptionData.length === 0) {
        throw new Error(`No consumption-based GHG data found for ${location} in ${tableName}`);
      }

      consumptionData.forEach((row) => {
        const hhcount = row.hhcount || 1;
        const perHouseholdMt =
          ((row.transportation_total_emissions || 0) +
            (row.housing_total_emissions || 0) +
            (row.food_total_emissions || 0) +
            (row.goods_total_emissions || 0) +
            (row.services_total_emissions || 0)) /
          1_000_000;
        const total = perHouseholdMt * hhcount;

        consumptionByYear[row.year] = total;
        consumptionPerHouseholdByYear[row.year] = perHouseholdMt;
      });
    }
    
    // Calculate sector-based totals for all years
    const sectorByYear: { [year: number]: number } = {};
    sectorBasedResult.chartData.labels.forEach((yearLabel: any, index: number) => {
      const year = parseInt(yearLabel.toString());
      let total = 0;
      
      sectorBasedResult.chartData.datasets.forEach((dataset: any) => {
        const value = dataset.data[index] as number;
        if (typeof value === 'number' && !isNaN(value)) {
          total += value;
        }
      });
      
      sectorByYear[year] = total;
    });
    
    // Get all available years (intersection of both datasets)
    const allYears = Array.from(new Set([
      ...Object.keys(sectorByYear).map(Number),
      ...Object.keys(consumptionByYear).map(Number)
    ])).sort((a, b) => a - b);
    
    // Prepare data arrays for chart
    const sectorData: number[] = [];
    const consumptionDataArray: number[] = [];
    const years: number[] = [];
    
    allYears.forEach(year => {
      if (sectorByYear[year] !== undefined && consumptionByYear[year] !== undefined) {
        years.push(year);
        sectorData.push(sectorByYear[year]);
        consumptionDataArray.push(consumptionByYear[year]);
      }
    });
    
    // Calculate latest year metrics for comparison
    const latestYear = Math.max(...years);
    const latestSectorTotal = sectorByYear[latestYear] || 0;
    const latestConsumptionTotal = consumptionByYear[latestYear] || 0;
    const consumptionPerHouseholdMtCo2e = consumptionPerHouseholdByYear[latestYear] ?? null;

    // Prepare chart data for line chart
    const chartData = {
      labels: years,
      datasets: [
        {
          label: 'Sector-Based Inventory',
          data: sectorData,
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderColor: '#3b82f6',
          borderWidth: 3,
          fill: false,
          tension: 0.2,
          pointRadius: 4,
          pointHoverRadius: 6
        },
        {
          label: 'Consumption-Based Inventory',
          data: consumptionDataArray,
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderColor: '#10b981',
          borderWidth: 3,
          fill: false,
          tension: 0.2,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    };
    
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 10,
          bottom: 10,
          left: 10,
          right: 10
        }
      },
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 15,
            font: {
              size: 13
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const value = context.parsed.y;
              const label = context.dataset.label;
              return `${label}: ${value.toLocaleString()} MT CO₂e`;
            }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Year',
            font: {
              size: 13,
              weight: 'bold' as const
            },
            padding: { top: 10, bottom: 5 }
          },
          ticks: {
            font: {
              size: 12
            },
            maxRotation: 45,
            minRotation: 45
          },
          grid: {
            display: false
          }
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Total Emissions (MT CO₂e)',
            font: {
              size: 13,
              weight: 'bold' as const
            },
            padding: { top: 5, bottom: 10 }
          },
          ticks: {
            font: {
              size: 12
            },
            callback: (value: any) => Number(value).toLocaleString()
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          }
        }
      }
    };
    
    const result: InventoryComparisonResult = {
      chartData,
      chartOptions,
      sectorBasedTotal: latestSectorTotal,
      consumptionBasedTotal: latestConsumptionTotal,
      consumptionPerHouseholdMtCo2e,
      comparisonCompatibility: 'different_accounting_boundaries',
      canDifference: false,
      latestYear,
    };
    
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching inventory comparison data:', error);
    throw error;
  }
}
